import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, User, Bot, Loader2 } from "lucide-react";
import { authAxios } from "../utils/auth";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import { formatInTimezone } from "../utils/timezone";

const PAGE_SIZE = 50;

/**
 * Chat Review — reconstructs a session's user/assistant transcript.
 * Admin-only. Backed by /admin/plog/session/{sid}/chat-messages which
 * auto-routes between eroscoach_messages and chat_messages by session
 * prefix, and filters out any system/tool roles.
 */
export default function PromptLogChatReview() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();
  const userTimezone = user?.timezone || "UTC";

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [sessionTitle, setSessionTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(
    async (skip, append) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await authAxios.get(
          `/admin/plog/session/${sessionId}/chat-messages?limit=${PAGE_SIZE}&skip=${skip}`
        );
        setTotal(res.data.total || 0);
        setSessionTitle(res.data.session_title || null);
        setHasMore(!!res.data.has_more);
        setMessages((prev) =>
          append ? [...prev, ...(res.data.messages || [])] : res.data.messages || []
        );
      } catch (e) {
        console.error("chat-review fetch failed:", e);
        setError(
          e?.response?.data?.detail ||
            "Failed to load messages. Admin access required."
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    fetchPage(0, false);
  }, [fetchPage]);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    fetchPage(messages.length, true);
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* ==================== Header ==================== */}
      <header
        className="border-b border-divine-gold/20 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50"
        data-testid="chat-review-header"
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
              data-testid="chat-review-back-btn"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-white font-medium truncate">
                {sessionTitle || "Chat Review"}
              </h1>
              <p className="text-white/40 text-xs truncate font-mono">
                {sessionId}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => fetchPage(0, false)}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              title="Refresh"
              data-testid="chat-review-refresh-btn"
              disabled={loading}
            >
              <RefreshCw
                className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <AdminMenu />
            <MainMenu />
          </div>
        </div>
      </header>

      {/* ==================== Body ==================== */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading && messages.length === 0 ? (
          <div
            className="flex items-center justify-center py-24 text-white/50"
            data-testid="chat-review-loading"
          >
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading transcript...
          </div>
        ) : error ? (
          <Card
            className="bg-red-500/10 border-red-500/30 p-6 text-red-300"
            data-testid="chat-review-error"
          >
            {error}
          </Card>
        ) : messages.length === 0 ? (
          <Card
            className="bg-white/5 border-white/10 p-10 text-center text-white/50"
            data-testid="chat-review-empty"
          >
            No user/assistant messages found for this session.
          </Card>
        ) : (
          <>
            <div className="mb-4 text-white/50 text-xs">
              Showing {messages.length} of {total} messages
            </div>
            <ul
              className="space-y-4"
              data-testid="chat-review-messages"
            >
              {messages.map((m, idx) => (
                <MessageRow
                  key={`${m.session_id}-${idx}-${m.created_at || ""}`}
                  message={m}
                  timezone={userTimezone}
                />
              ))}
            </ul>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="bg-divine-gold hover:bg-divine-gold-light text-black font-medium tracking-widest px-8"
                  data-testid="chat-review-load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>Load {Math.min(PAGE_SIZE, total - messages.length)} more</>
                  )}
                </Button>
              </div>
            )}

            {!hasMore && messages.length > 0 && (
              <div className="text-center text-white/30 text-xs mt-8">
                End of transcript
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MessageRow({ message, timezone }) {
  const isUser = message.role === "user";
  const Icon = isUser ? User : Bot;
  const label = isUser ? "USER" : "ASSISTANT";
  const bubbleTone = isUser
    ? "bg-white/[0.04] border-white/10"
    : "bg-divine-gold/[0.04] border-divine-gold/20";
  const iconTone = isUser
    ? "bg-white/10 text-white/70"
    : "bg-divine-gold/20 text-divine-gold";

  return (
    <li
      className={`rounded-xl border p-4 ${bubbleTone}`}
      data-testid={`chat-review-msg-${message.role}`}
    >
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center ${iconTone}`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <span
            className={`text-xs font-semibold tracking-widest ${
              isUser ? "text-white/70" : "text-divine-gold"
            }`}
          >
            {label}
          </span>
        </div>
        {message.created_at && (
          <span className="text-white/30 text-[11px] font-mono">
            {formatInTimezone(message.created_at, timezone)}
          </span>
        )}
      </div>
      <div className="text-white/85 whitespace-pre-wrap break-words leading-relaxed text-sm sm:text-[15px]">
        {message.content || <span className="italic text-white/30">(empty)</span>}
      </div>
    </li>
  );
}
